import React, { useEffect, useState } from 'react';
import { Link2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Item, ItemType } from '@/stores/canvasStore';
import { StickyNote } from './StickyNote';
import { TextCard } from './TextCard';
import { ImageCard } from './ImageCard';
import { LinkCard } from './LinkCard';
import { VideoCard } from './VideoCard';
import { FileCard } from './FileCard';
import { DocumentCard } from './DocumentCard';

interface SyncedItemCardProps {
  item: Item;
}

export function SyncedItemCard({ item }: SyncedItemCardProps) {
  const [sourceItem, setSourceItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  const content = item.content as { source_item_id?: string; source_board_id?: string };
  
  useEffect(() => {
    const fetchSource = async () => {
      const sourceId = content.source_item_id;
      if (!sourceId) {
        setError(true);
        setLoading(false);
        return;
      }
      
      try {
        const { data, error: rpcError } = await supabase
          .rpc('get_synced_item_source', { source_id: sourceId });
        
        if (rpcError) {
          console.error('Error fetching synced item source:', rpcError);
          setError(true);
        } else if (data && data.length > 0) {
          const sourceData = data[0];
          setSourceItem({
            ...sourceData,
            item_type: sourceData.item_type as ItemType,
          });
        } else {
          // Source was deleted
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching synced item source:', err);
        setError(true);
      }
      
      setLoading(false);
    };
    
    fetchSource();
  }, [content.source_item_id]);
  
  if (loading) {
    return (
      <div className="w-full h-full rounded-lg bg-muted/50 animate-pulse flex items-center justify-center border border-border">
        <Link2 className="w-6 h-6 text-muted-foreground animate-spin" />
      </div>
    );
  }
  
  if (error || !sourceItem) {
    return (
      <div className="w-full h-full rounded-lg bg-muted/30 border border-dashed border-border flex flex-col items-center justify-center gap-2 p-4">
        <AlertCircle className="w-8 h-8 text-muted-foreground" />
        <span className="text-xs text-muted-foreground text-center">
          Source deleted
        </span>
      </div>
    );
  }
  
  // Render the source item's content based on its type
  const renderSourceContent = () => {
    // Create a virtual item with source content but using synced item's dimensions
    const virtualItem: Item = {
      ...sourceItem,
      id: item.id, // Use synced item's ID for selection etc.
      position_x: item.position_x,
      position_y: item.position_y,
      width: item.width,
      height: item.height,
    };
    
    switch (sourceItem.item_type) {
      case 'sticky':
        return <StickyNote item={virtualItem} />;
      case 'text_card':
        return <TextCard item={virtualItem} />;
      case 'image':
        return <ImageCard item={virtualItem} />;
      case 'link':
        return <LinkCard item={virtualItem} />;
      case 'video':
        return <VideoCard item={virtualItem} />;
      case 'file':
        return <FileCard item={virtualItem} />;
      case 'document':
        return <DocumentCard item={virtualItem} />;
      default:
        return <div className="p-4 text-muted-foreground">Unsupported type</div>;
    }
  };
  
  return (
    <div className="relative w-full h-full group">
      {/* Sync indicator badge */}
      <div className="absolute -top-2 -left-2 z-10 p-1.5 rounded-full bg-violet-500 shadow-md">
        <Link2 className="w-3 h-3 text-white" />
      </div>
      
      {/* Read-only overlay on hover */}
      <div className="absolute inset-0 z-[5] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-black/70 text-white text-xs whitespace-nowrap">
          Synced • View only
        </div>
      </div>
      
      {/* Prevent interactions with content */}
      <div className="pointer-events-none">
        {renderSourceContent()}
      </div>
    </div>
  );
}
